import { Eye, EyeOff, LocateFixed, X } from "lucide-react";
import type { Ref } from "preact";
import type { MapLocation, MapStand } from "../shared/types";
import { VenueMapThumbnail } from "./VenueMapThumbnail";
import type { VenueMapLogos } from "./useVenueMapLogos";

const categoryLabels = {
  exhibitor: "Exhibitor",
  stage: "Stage",
  lounge: "Lounge",
  facility: "Facility",
  unassigned: "Booth",
};

export function VenueMapSelection({
  location,
  stand,
  onFocus,
  onDismiss,
  onToggleVisibility,
  visibilityDisabled = false,
  hidden = false,
  logos,
  elementRef,
}: {
  location: MapLocation;
  stand?: MapStand;
  logos?: VenueMapLogos;
  onFocus: () => void;
  onDismiss: () => void;
  /** Only supplied by an editable admin preview. */
  onToggleVisibility?: () => void;
  visibilityDisabled?: boolean;
  hidden?: boolean;
  elementRef?: Ref<HTMLElement>;
}) {
  const name = stand?.name || stand?.shortName || location.label;
  return (
    <section
      ref={elementRef}
      className="venue-map-selection"
      aria-label="Selected location"
    >
      <VenueMapThumbnail stand={stand} label={location.label} logos={logos} />
      <div className="venue-map-selection-copy">
        <p className="venue-map-selection-detail">
          {stand
            ? `${stand.id} · ${categoryLabels[stand.category]}`
            : "Location"}
          {hidden && (
            <span className="venue-map-selection-hidden"> · Hidden</span>
          )}
        </p>
        <h3>{name}</h3>
        <div className="venue-map-selection-actions">
          <button
            type="button"
            className="venue-map-selection-focus"
            onClick={onFocus}
          >
            <LocateFixed size={14} aria-hidden="true" />
            <span>Show on map</span>
          </button>
          {onToggleVisibility && (
            <button
              type="button"
              className="venue-map-selection-visibility"
              disabled={visibilityDisabled}
              aria-label={`${hidden ? "Show" : "Hide"} ${stand ? "booth" : "place"}`}
              onClick={onToggleVisibility}
            >
              {hidden ? (
                <Eye size={14} aria-hidden="true" />
              ) : (
                <EyeOff size={14} aria-hidden="true" />
              )}
              <span>{hidden ? "Show" : "Hide"}</span>
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        className="venue-map-selection-dismiss"
        aria-label="Dismiss selected location"
        onClick={onDismiss}
      >
        <X size={18} aria-hidden="true" />
      </button>
    </section>
  );
}
